import React, {useEffect, useReducer, useState} from "react";
import LoadingComponent from "../pages/LoadingComponent";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from "@mui/material";
import {firestore} from "../firebase";
//import {firestore, database} from "../firebase";
import Paper from "@mui/material/Paper";
import {sortString} from "./Utils";
import {useGroup} from "../contexts/GroupContext";
import ExpandableRow from "./ExpandableRow";
import Typography from "@mui/material/Typography";
import {makeStyles} from "@mui/styles";
import {useTheme} from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";


const GoalComponent = (props) => {
    const {group, botNames, botSerials, connectedStatuses} = useGroup()
    const [isReady, setIsReady] = useState(false);
    const [tableData, dispatch] = useReducer(tableDataReducer, {}); //Updates robotMessages on snapshot update
    const [componentOpen, setComponentOpen] = useState(true)

    function tableDataReducer(tableDataState, newData) {
        let newState = JSON.parse(JSON.stringify(tableDataState)) //Copy to avoid mutating state directly
        if (newState[newData.id] === undefined) { //If the robot id hasn't been seen before, create a new entry
            newState[newData.id] = {}
        }
        switch (newData.type) {
            case "reset":
                //Completely reset the goal state
                return {}
            case "mod":
                newState[newData.id] = newData.data
                break;
            case "sort":
                newState = newData.data //Set data to the sorted version
                break;
            default:
                break;
        }
        return newState
    }

    //sorting
    const [orderBy, setOrderBy] = useState("name")
    const [sortDirection, setSortDirection] = useState("desc");

    //Creates the sorting function for each column specified by id
    const createSort = (id) => {
        return (() => {
            let data = JSON.parse(JSON.stringify(tableData)) //Deep copy
            setOrderBy(id)
            let arrayData = Object.entries(data)
            arrayData.sort((a, b) => {
                if (sortDirection === "asc") {
                    setSortDirection("desc")
                } else {
                    let temp = a
                    a = b
                    b = temp
                    setSortDirection("asc")
                }
                switch (id) { //Check what the user wants to sort by
                    case "name":
                        return sortString(a[0], b[0])
                    case "goal":
                        return sortString(a[1].active.predicate, b[1].active.predicate)
                    case "status":
                        return sortString(a[1].active.status, b[1].active.status)
                    default:
                        return 0;
                }
            })
            dispatch({data: Object.fromEntries(arrayData), type: "sort"})
        })
    }

    //Constructor
    useEffect(() => {
        const col = firestore.collection(`groups/${group}/robots`)

        const generateTableEntry = async (robotSerial, robotName) => {
            const goalDoc = col.doc(robotSerial).collection("goals").doc("gs")

            return goalDoc
                .onSnapshot(doc => {
                    dispatch({data: doc.data(), id: robotName, type: "mod"})
                });
        }

        //Generates all table data by iterating over each robot
        let currentUnsubscribers = []
        const generateTableData = async (names) => {
            for (const name of names) {
                let index = botNames.indexOf(name)
                let id = botSerials[index]
                await generateTableEntry(id, name).then(entry => {
                    if (entry !== undefined) {
                        currentUnsubscribers.push(entry)
                    }
                })
            }
        }

        //Generates all data needed before rendering
        generateTableData(botNames)

        return () => {
            currentUnsubscribers.forEach((unsub) => {
                unsub()
            })

            //Manually clears the messages for all robot profiles - this will only work
            dispatch({type: "reset"})
        }
    }, [group, botNames, botSerials])

    //When the table has data, we're ready to render
    useEffect(() => {
        //EW: prevent this from rendering upon retrieval of each agent's data, wait until all data is set
        if (tableData !== undefined && Object.keys(tableData).length === botNames.length && Object.keys(tableData).length === botSerials.length && Object.keys(connectedStatuses).length === botSerials.length) {
            setIsReady(true)
        } else {
            setIsReady(false)
        }
    }, [tableData, botNames, botSerials, connectedStatuses])

    //Creates a list of expandable rows based on the table data
    const renderTableContent = () => {
        let rows = []
        if (Object.keys(tableData).length === 0) {
            //TODO: this case means no robot has been connected yet, not that there are not goals
            return (
                <TableRow>
                    <TableCell colSpan={5}>
                        <Typography variant="body1">The robot currently has no goals. </Typography>
                    </TableCell>
                </TableRow>
            )
        }
        for (const [key, value] of Object.entries(tableData)) {
            let serialKey = botSerials[botNames.indexOf(key)]
            if (value !== undefined) {
            rows.push(<ExpandableRow key={key} name={key} data={value} connected={connectedStatuses[serialKey]}/>)
            }
        }
        return rows
    }

    //Information for the table header
    const headCells = [
        {id: 'name', label: 'Name'},
        {id: 'connected', label: 'Online'},
        {id: 'goal', label: 'Task'},
        {id: 'status', label: 'Status'},
        {id: 'time', label: 'Time'},
    ];

    function handleComponentOpen(open) {
        setComponentOpen(!open)
    }

    if (isReady) {

        return (
            <TableContainer component={Paper}>
                <Accordion expanded={componentOpen}>
                    <AccordionSummary  onClick={() => handleComponentOpen(componentOpen)}>
                        <Grid container direction="row"
                              justifyContent="space-between"
                              alignItems="center">
                            <Grid item>
                                <Typography variant="h1" color={'accordionHeader'}>Tasks</Typography>
                            </Grid>
                            <Grid item>
                                <IconButton size="small" onClick={() => handleComponentOpen(componentOpen)}>
                                    {componentOpen ? <KeyboardArrowUpIcon fontSize="large"/> :
                                        <KeyboardArrowDownIcon fontSize="large"/>}
                                </IconButton>
                            </Grid>
                        </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell/>
                                    {headCells.map((headCell) => (
                                        <TableCell key={headCell.id}>
                                            <TableSortLabel
                                                active={orderBy === headCell.id}
                                                direction={orderBy === headCell.id ? sortDirection : 'asc'}
                                                onClick={createSort(headCell.id)}
                                            >
                                                {headCell.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    <TableCell/>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {renderTableContent()}
                            </TableBody>
                        </Table>
                    </AccordionDetails>
                </Accordion>
            </TableContainer>
        )
    } else {
        return (
            <LoadingComponent/>
        )
    }
}


export default GoalComponent